import Preloader from 'components/Dashboard/Analytics/ui/AnalyticsDashboard/Preloader';
import DownloadChartModal from 'components/Dashboard/Analytics/ui/DownloadChartModal/DownloadChartModal';
import { createDate } from 'components/Dashboard/Analytics/ui/MentionsByTopic/ChartDataTable';
import FiltersBar from 'components/Dashboard/Analytics/ui/AnalyticsFilters/FiltersBar';
import SentimentOverTimeChart from 'components/Dashboard/Analytics/ui/SentimentOverTime/SentimentOverTimeChart';
import useCallSentimentOverTime from 'components/Dashboard/Analytics/useCallSentimentOverTime';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { store } from 'components/Store';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

const StakeholderSentimentChangesGraph = ({
  sentimentChangesState,
  loadingSentimentChangesState,
  noSearchCondition,
  StakeholderInformation,
  setRightPanelState,
  stateSentimentChangesAnalytics,
}) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists } = state;

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  //AE: The idea is to keep this state on parent component in case we need to know if both graphs are empty and ready
  const { dataSentimentChanges, setDataSentimentChanges } = sentimentChangesState ?? {};
  const { sentimentChangesAnalyticsState, setSentimentChangesAnalyticsState } = stateSentimentChangesAnalytics ?? {};
  const { isLoading, setIsLoading } = loadingSentimentChangesState ?? {};
  const { name } = StakeholderInformation;
  const { labels, rawData } = dataSentimentChanges ?? {};

  const { currentView, activeDateInterval } = sentimentChangesAnalyticsState ?? {};

  const topicParameter = getUrlParam('topic-id');
  const searchParameter = getUrlParam('search');
  const topic = keywordsLists?.find((item) =>
    topicParameter === 'all' ? item?.id === null : item?.id === parseInt(topicParameter)
  );
  const { callSentimentOverTime } = useCallSentimentOverTime({
    selectedCountriesProps: [
      { name: 'MPs & peers', value: 'UK' },
      { name: 'MSs (Wales)', value: 'Wales' },
      { name: 'MSPs (Scotland)', value: 'Scotland' },
      { name: 'Northern Ireland', value: 'NorthernIreland' },
    ],
  });

  const params = useParams();
  const { id } = params;
  const chartSentimentItem = useRef(null);
  const downloadButton = useRef(null);
  const chartSection = useRef(null);

  const initialCall = useRef(null);
  initialCall.current = async ({ source }) => {
    if (!noSearchCondition) {
      try {
        setIsLoading(true);
        const sentimentChanges = await callSentimentOverTime({
          view: currentView,
          dateInterval: activeDateInterval?.filter,
          freeSearch: searchParameter,
          stakeholderId: id,
          source,
        });
        if (!!sentimentChanges) {
          setDataSentimentChanges(sentimentChanges);
          setIsLoading(false);
        }
      } catch (error) {}
    }
  };

  const modifyFiltersBarFunction = ({ property, newValue }) => {
    const newState =
      property === 'CompleteState' ? newValue : { ...sentimentChangesAnalyticsState, [property]: newValue };
    setSentimentChangesAnalyticsState(newState);
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current({ source });
    return () => {
      source.abort('Stakeholder sentiment changes loading cancelled by the user');
    };
  }, [topicParameter, searchParameter, currentView, activeDateInterval]);

  const compiledData = () => {
    if (!!rawData) {
      const result = {};

      for (const sentiment of Object.values(rawData)) {
        for (const category of Object.values(sentiment)) {
          for (const { date, value } of category) {
            result[date] = (result[date] || 0) + value;
          }
        }
      }
      return result;
    }
  };

  const sentimentOverTimeChartClick = (e) => {
    const points = chartSentimentItem.current?.getElementsAtEventForMode(e, 'nearest', { intersect: false }, true);
    if (points?.length) {
      const firstPoint = points[0];
      const position = firstPoint?.element?.$context?.dataIndex;
      const value = compiledData()?.[labels[position]];
      if (value > 0) {
        setRightPanelState({
          type: 'SentimentChanges',
          open: true,
          date: labels[position],
          rawData,
          text: `${thousandCommas(value)} ${`mention${value === 1 ? '' : 's'}`} by ${name} ${createDate(
            currentView,
            labels[position]
          )}`,
          dataKey: 'totalCell',
          noResults: value === 0,
          sentiment: null,
        });
      }
    }
  };
  return (
    <>
      <div className='border py-3 rounded mt-3'>
        <div className='px-4 '>
          <div className='mb-2'>
            {/*stakeholder-sentiment-filters-bar stakeholder-sentiment-graph-filters */}
            <h3 className='title-h5-m-bold my-3 nowrap-item flex-grow-1 sentiment-changes-chart-title'>
              Sentiment over time
            </h3>
          </div>
          <div className='pb-3 justify-content-between flex-centered stakeholder-sentiment-filters-bar'>
            <div className='overflow-hidden flex-grow-1 stakeholder-sentiment-filters-container'>
              <div className='w-100 overflow-auto '>
                <FiltersBar
                  typeOfChartSections={true}
                  chartName={'Sentiment changes'}
                  analyticsState={sentimentChangesAnalyticsState}
                  modifyFiltersBar={modifyFiltersBarFunction}
                />
              </div>
            </div>

            <button
              ref={downloadButton}
              className='general-button circle-button circle-button-36 border'
              disabled={isLoading}
              onClick={() => {
                downloadButton.current.blur();
                setShowDownloadModal(true);
              }}
              data-tooltip-id={'general-tooltip'}
              data-tooltip-content={'Download chart'}
            >
              <span className='icon-download' />
            </button>
          </div>
        </div>
        {isLoading ? (
          <Preloader />
        ) : (
          <div className='pt-3 pr-3' ref={chartSection}>
            <SentimentOverTimeChart
              data={dataSentimentChanges}
              dashboard
              analyticsState={sentimentChangesAnalyticsState}
              ref={chartSentimentItem}
              clickFunction={sentimentOverTimeChartClick}
            />
          </div>
        )}
      </div>

      <DownloadChartModal
        setIsOpen={setShowDownloadModal}
        isOpen={showDownloadModal}
        chartSection={chartSection}
        type={'Sentiment changes'}
        data={dataSentimentChanges}
        chartRef={chartSentimentItem}
        analyticsState={sentimentChangesAnalyticsState}
        setChartOnDownload={() => {}}
        initialHeader={`Political sentiment by ${name}: ${searchParameter ?? topic?.name}`}
      />
    </>
  );
};

export default StakeholderSentimentChangesGraph;
